import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Edit,
  Trash2,
  User,
} from "lucide-react";
import { Card } from "../../../../shadcn/ui/card";
import { Badge } from "../../../../shadcn/ui/badge";
import { Button } from "../../../../shadcn/ui/button";
import StatusBadge from "./StatusBadge";
import { FeatureAccess } from "../../../../utils/featureAccessControl";
import * as localService from "../../../../services/localService";

// Organization Details Component
const OrganizationDetails = ({ company, orgData, onEditOrganization, onDeleteOrganization, canManageOrgs }) => {
  const [imgErrors, setImgErrors] = useState({});
  const [imgLoaded, setImgLoaded] = useState({});
  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const creatorProfile = orgData?.creatorProfile;
  const adminProfiles = orgData?.adminProfiles?.length ? orgData.adminProfiles : creatorProfile ? [creatorProfile] : [];

  const getAvatarUrl = profile => {
    if (profile.imageUrl) return profile.imageUrl;
    const name = `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || "User";
    return "https://ui-avatars.com/api/?name=" + encodeURIComponent(name) + "&background=random";
  };

  const getFullName = profile => `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || "N/A";

  return (
    <div className="mt-6 space-y-6">
      {/* Actions */}
      {canManageOrgs && (
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEditOrganization(company)}
            className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Organization
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDeleteOrganization(company)}
            className="border-red-600 text-red-600 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Organization
          </Button>
        </div>
      )}

      {/* Admin Profiles */}
      {adminProfiles.length > 0 && (
        <div className="pb-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            Admin Profiles{" "}
            <Badge variant="default" className="text-[10px] px-1.5 py-0">
              {adminProfiles.length}
            </Badge>
          </h3>
          <div className="space-y-3">
            {adminProfiles.map(profile => (
              <div key={profile.id} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 mt-0.5">
                  {!imgErrors[profile.id] ? (
                    <img
                      src={getAvatarUrl(profile)}
                      alt={getFullName(profile)}
                      className={`w-full h-full object-cover`}
                      onError={e => {
                        if (profile.imageUrl && !imgLoaded[profile.id]) {
                          e.target.src = getAvatarUrl({ ...profile, imageUrl: null });
                          setImgLoaded(prev => ({ ...prev, [profile.id]: true }));
                        } else {
                          setImgErrors(prev => ({ ...prev, [profile.id]: true }));
                        }
                      }}
                    />
                  ) : (
                    <User className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-900">{getFullName(profile)}</p>
                    {creatorProfile?.id === profile.id && (
                      <Badge variant="default" className="text-[10px] px-1.5 py-0">
                        Creator
                      </Badge>
                    )}
                  </div>
                  {profile.username && <p className="text-xs text-gray-500">@{profile.username}</p>}
                  {profile.emailAddress && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <p className="text-xs text-gray-600 truncate">{profile.emailAddress}</p>
                    </div>
                  )}
                  {profile.msisdn && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <p className="text-xs text-gray-600">{profile.msisdn}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Organization Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Contact Information</h3>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Primary Email</p>
                <p className="text-sm text-gray-900 truncate">{company.contactEmail || "N/A"}</p>
              </div>
            </div>

            {company.alternateEmail && (
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Alternate Email</p>
                  <p className="text-sm text-gray-900 truncate">{company.alternateEmail}</p>
                </div>
              </div>
            )}

            {company.contactMsisdn && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Phone Number</p>
                  <p className="text-sm text-gray-900">{company.contactMsisdn}</p>
                </div>
              </div>
            )}

            {company.webUrl && (
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Website</p>
                  <a
                    href={company.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 truncate block"
                  >
                    {company.webUrl}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location & Other Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Location & Details</h3>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm text-gray-900">
                  {company.address || "N/A"}
                  {company.city && `, ${company.city}`}
                  {company.region && `, ${company.region}`}
                </p>
                {company.country?.name && (
                  <p className="text-sm text-gray-600 mt-1">
                    {company.country.flag} {company.country.name}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Organization Code</p>
                <p className="text-sm text-gray-900 font-mono">{company.code || "N/A"}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Organization Type</p>
                <p className="text-sm text-gray-900">{company.type?.name || "N/A"}</p>
              </div>
            </div>

            {company.spaceType && (
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Space Type</p>
                  <Badge className="mt-1">{company.spaceType}</Badge>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Created</p>
                <p className="text-sm text-gray-900">{formatDate(company.createdAt)}</p>
              </div>
            </div>

            {company.modifiedAt && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Last Modified</p>
                  <p className="text-sm text-gray-900">{formatDate(company.modifiedAt)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Company Card Component
const CompanyCard = ({
  company,
  isExpanded,
  onToggle,
  expandedOrgData,
  onEditOrganization,
  onDeleteOrganization,
  canManageOrgs,
}) => {
  const formatLastActive = dateString => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const lastActive = formatLastActive(company.modifiedAt);

  return (
    <Card className="p-6 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Company Logo/Icon */}
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
            {company.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                className="object-cover w-12 h-12 rounded-lg"
                onError={e => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
            ) : null}
            <Building2 className="w-6 h-6 text-gray-600" style={{ display: company.logoUrl ? "none" : "block" }} />
          </div>

          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-semibold text-gray-900">{company.name}</h2>
              <StatusBadge status={company.status} />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
              <span>{company.type?.name || "Organization"}</span>
              <span>•</span>
              <span>Last modified: {lastActive}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onToggle} className="p-2">
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <OrganizationDetails
          company={company}
          orgData={expandedOrgData?.[company.id]}
          onEditOrganization={onEditOrganization}
          onDeleteOrganization={onDeleteOrganization}
          canManageOrgs={canManageOrgs}
        />
      )}
    </Card>
  );
};

// Main Company Accordion Component
const CompanyAccordion = ({
  companies,
  openCompanyAccordion,
  onToggleAccordion,
  expandedOrgData,
  onEditOrganization,
  onDeleteOrganization,
}) => {
  const currentUser = localService.getUser();
  const canManageOrgs = FeatureAccess.canEditUsers(currentUser);
  const toggleAccordion = companyId => {
    onToggleAccordion(companyId);
  };

  if (!companies || companies.length === 0) {
    return (
      <div className="py-8 text-center">
        <Building2 className="mx-auto mb-4 w-12 h-12 text-gray-400" />
        <p className="text-gray-500">No organizations to display</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {companies.map(company => (
        <CompanyCard
          key={company.id}
          company={company}
          isExpanded={openCompanyAccordion === company.id}
          onToggle={() => toggleAccordion(company.id)}
          expandedOrgData={expandedOrgData}
          onEditOrganization={onEditOrganization}
          onDeleteOrganization={onDeleteOrganization}
          canManageOrgs={canManageOrgs}
        />
      ))}
    </div>
  );
};

export default CompanyAccordion;
